import { useCallback, useEffect, useState } from 'react'
import { useStytch } from '@stytch/react'
import { PROVIDER_EVENT_NAMES, tracking } from 'lib/segment/tracking'
import { postAuthOauthLogin } from 'api/generated/auth/auth'
import type { AuthTokenApiContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

// Annotated rather than suppressed: `{ onSuccess, onFailure } = {}` with no annotation
// is TS2525, and silencing that made the parameter an error type — which in turn made
// every call site unchecked, so four `@ts-expect-error TS2345` directives in the test
// were suppressing nothing.
type UseStytchOAuthOptions = {
  onSuccess?: (response: AuthTokenApiContract) => void
  onFailure?: (error: unknown) => void
}

export function useStytchOAuth({ onSuccess, onFailure }: UseStytchOAuthOptions = {}) {
  const stytch = useStytch()
  // Seed from the URL so the login form doesn't flash before the verification
  // effect runs when we return from the Google redirect with a token.
  const [isVerifying, setIsVerifying] = useState(() =>
    Boolean(new URLSearchParams(window.location.search).get('token')),
  )

  const startGoogleOAuth = useCallback(() => {
    stytch.oauth.google.start({
      login_redirect_url: window.location.origin,
    })
  }, [stytch])

  // After the Stytch Google redirect we receive a one-time OAuth token in the URL.
  // Instead of exchanging it for a Stytch session client-side, hand it to our own
  // backend (/auth/oauth/login), which verifies it with Stytch, confirms a matching
  // provider exists, and returns our AccessToken.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) return

    // isVerifying is already seeded true from the URL token above, so no need to
    // set it again here (and doing so synchronously in an effect is discouraged).
    postAuthOauthLogin({ Token: token })
      .then((response) => {
        tracking.trackEvent(PROVIDER_EVENT_NAMES.GOOGLE_OAUTH_LOGIN)
        onSuccess?.(response)
      })
      .catch((err) => {
        // Keep the loading state on success — the caller navigates away. On
        // failure, clear it so the login form returns.
        setIsVerifying(false)
        onFailure?.(err)
      })
  }, [onSuccess, onFailure])

  return { startGoogleOAuth, isVerifying }
}
